import { useState, useEffect, useCallback } from 'react'
import { getFeatureFlags } from '@/services/api/featureFlags'
import { DEFAULT_FEATURE_FLAGS, type FeatureFlags } from '@/types/featureFlags'
import { setFeatureFlagCache } from '@/utils/categories'
import { useFeatureFlagStore } from '@/store/useFeatureFlagStore'

/**
 * Loads admin-managed feature flags once and pushes them into the module
 * cache in `categories.ts` plus the reactive feature-flag store. Mount at
 * the app root so both are populated app-wide. Defaults to the cautious
 * value on missing doc or fetch error.
 */
export function useFeatureFlags() {
  const [flags, setFlags] = useState<FeatureFlags>(DEFAULT_FEATURE_FLAGS)
  const [loading, setLoading] = useState(true)
  const setStoreFlags = useFeatureFlagStore((s) => s.setFlags)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const data = await getFeatureFlags()
      setFlags(data)
      setFeatureFlagCache(data)
      setStoreFlags(data)
    } catch (err) {
      console.error('Failed to fetch feature flags:', err)
      setFlags(DEFAULT_FEATURE_FLAGS)
      setFeatureFlagCache(DEFAULT_FEATURE_FLAGS)
      setStoreFlags(DEFAULT_FEATURE_FLAGS)
    } finally {
      setLoading(false)
    }
  }, [setStoreFlags])

  useEffect(() => {
    load()
  }, [load])

  return { flags, loading, refresh: load }
}
